/**
 * Create Document — Phase 2 (IDR live, PSLF/TPD coming soon).
 *
 * IDR wizard:
 *   1. Choose Template (IDR / PSLF / TPD)
 *   2. Review Auto-Filled — read-only display of every CaseFlow value
 *      that will be pre-filled in the PDF
 *   3. Complete Required — wizard-only inputs (SSN, request reason, plan,
 *      marital status, taxable income, IRS consent, form date) with a
 *      live "Missing N required fields" chip and Generate-enabled gate
 *   4. Preview/Save — post-generation result panel with Download / Print
 *
 * Sensitive-data rules:
 *   • Full SSN lives ONLY in component state; cleared on dialog close.
 *   • Never echoed in toast text or any logged field.
 *   • PSLF + TPD templates render the placeholder copy + Generate stays
 *     disabled because backend will 501 (and we mirror that intent in UI).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Sparkles, FileText, CheckCircle2, AlertCircle, Lock,
    ChevronLeft, ChevronRight, Download, Eye, Printer,
} from "lucide-react";
import { toast } from "sonner";

const STEPS = [
    { key: "choose", title: "Choose Template" },
    { key: "review", title: "Review Auto-Filled" },
    { key: "complete", title: "Complete Required" },
    { key: "preview", title: "Save & Download" },
];

const REQUEST_REASONS = [
    { v: "enter",         l: "Enter an IDR plan" },
    { v: "recertify",     l: "Annual recertification" },
    { v: "early_recalc",  l: "Early recalculation" },
    { v: "change_plan",   l: "Change to a different IDR plan" },
];
const PLANS = [
    { v: "ibr",  l: "Income-Based Repayment (IBR)" },
    { v: "paye", l: "Pay As You Earn (PAYE)" },
    { v: "icr",  l: "Income-Contingent Repayment (ICR)" },
];

export default function CreateDocumentDialog({ open, onOpenChange, client, onGenerated }) {
    const [step, setStep] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);   // post-gen response

    // Preview state — mirrors the working ClientDocumentsTab pattern
    // (preview-bytes endpoint → base64 → in-dialog iframe). No window.open,
    // no blob popups (those get blocked when fired from async callbacks).
    const [preview, setPreview] = useState(null); // { mime, dataUrl, filename, printOnLoad }

    // IDR wizard inputs (component-state only; cleared on dialog close)
    const [w, setW] = useState(makeBlankWizard());

    useEffect(() => {
        if (!open) {
            // CLEAR ALL SENSITIVE STATE when dialog closes
            setStep(0); setSelected(null); setW(makeBlankWizard()); setResult(null); setPreview(null);
            return;
        }
        api.get("/document-templates")
            .then(r => setTemplates(r.data || []))
            .catch(e => toast.error(formatApiError(e)));
    }, [open]);

    const isIDR = selected?.template_key === "idr_plan_request_2028";
    const isPSLF = selected?.template_key === "pslf_application_2026";
    const isTPD = selected?.template_key === "tpd_application_2027";
    const supported = isIDR || isPSLF || isTPD;
    const missing = useMemo(() => {
        if (step < 2) return [];
        if (isIDR) return computeMissingIDR(client, w);
        if (isPSLF) return computeMissingPSLF(client, w);
        if (isTPD) return computeMissingTPD(client, w);
        return [];
    }, [client, w, isIDR, isPSLF, isTPD, step]);

    const canNext =
        step === 0 ? !!selected
        : step === 1 ? !!supported
        : step === 2 ? (supported ? missing.length === 0 : false)
        : false;

    const attemptGenerate = async () => {
        if (!selected || !supported) return;
        setBusy(true);
        try {
            const r = await api.post(`/clients/${client.id}/documents/generate`, {
                template_key: selected.template_key,
                ...w,
            });
            setResult(r.data);
            setStep(3);
            toast.success(`${selected.name} generated and saved.`);
            // Clear all full-SSN values from memory immediately after gen
            setW(prev => ({ ...prev, borrower_full_ssn: "", spouse_full_ssn: "" }));
            onGenerated && onGenerated();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (e?.response?.status === 501) {
                toast.message("Coming in a future phase",
                    { description: typeof detail === "string" ? detail : "Not implemented yet." });
            } else {
                toast.error(typeof detail === "string" ? detail : formatApiError(e));
            }
        } finally { setBusy(false); }
    };

    const downloadGenerated = async () => {
        if (!result) return;
        try {
            const r = await api.get(
                `/clients/${client.id}/documents/${result.document_id}/download`,
                { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = result.filename; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const previewGenerated = () => loadPreviewBytes(false);
    const printGenerated = () => loadPreviewBytes(true);

    // Preview = same proven flow used by ClientDocumentsTab.openPreview:
    // /preview-bytes returns {mime, data_base64, filename} which we render
    // into an in-dialog iframe. No popup-blocker risk (no window.open).
    const loadPreviewBytes = async (printOnLoad = false) => {
        if (!result) return;
        try {
            const r = await api.get(`/clients/${client.id}/documents/${result.document_id}/preview-bytes`);
            const dataUrl = `data:${r.data.mime};base64,${r.data.data_base64}`;
            setPreview({ mime: r.data.mime, dataUrl, filename: r.data.filename || result.filename, printOnLoad });
        } catch (e) {
            const msg = formatApiError(e);
            if (msg.includes("415") || msg.toLowerCase().includes("preview")) {
                toast.message("Preview not available. Download to view.");
            } else toast.error(msg);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto" data-testid="create-doc-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Create Document</DialogTitle>
                    <DialogDescription>
                        Generate a federal application pre-filled with this client&apos;s data. {client?.client_name && <span className="font-medium text-gray-700">For: {client.client_name}</span>}
                    </DialogDescription>
                </DialogHeader>

                <Stepper step={step} />

                <div className="min-h-[300px] py-3">
                    {step === 0 && <TemplatePicker templates={templates} selected={selected} onSelect={setSelected} />}
                    {step === 1 && isIDR && <ReviewIDR client={client} />}
                    {step === 1 && isPSLF && <ReviewPSLF client={client} />}
                    {step === 1 && isTPD && <ReviewTPD client={client} />}
                    {step === 1 && !supported && <ComingSoonNotice template={selected} />}
                    {step === 2 && isIDR && <CompleteIDR w={w} setW={setW} missing={missing} />}
                    {step === 2 && isPSLF && <CompletePSLF w={w} setW={setW} client={client} missing={missing} />}
                    {step === 2 && isTPD && <CompleteTPD w={w} setW={setW} client={client} missing={missing} />}
                    {step === 3 && result && <ResultPanel result={result} onDownload={downloadGenerated} onPreview={previewGenerated} onPrint={printGenerated} />}
                </div>

                <DialogFooter className="flex items-center justify-between gap-2">
                    <Button variant="ghost" disabled={step === 0 || step === 3} onClick={() => setStep(s => Math.max(0, s - 1))} data-testid="wizard-back">
                        <ChevronLeft className="h-4 w-4 mr-1" /> Back
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wizard-cancel">{step === 3 ? "Close" : "Cancel"}</Button>
                        {step < 2 && (
                            <Button disabled={!canNext} onClick={() => setStep(s => s + 1)} data-testid="wizard-next">
                                Next <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        )}
                        {step === 2 && (
                            <Button disabled={!canNext || busy} onClick={attemptGenerate} data-testid="wizard-generate">
                                {busy ? "Generating…" : "Generate"}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>

            {/* Nested preview dialog — mirrors ClientDocumentsTab pattern.
                Rendered as sibling so it portals above the wizard. */}
            <PreviewDialog preview={preview} onClose={() => setPreview(null)} />
        </Dialog>
    );
}

function PreviewDialog({ preview, onClose }) {
    return (
        <Dialog open={!!preview} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="sm:max-w-[900px]" data-testid="result-preview-dialog">
                <DialogHeader>
                    <DialogTitle className="truncate">{preview?.filename}</DialogTitle>
                    <DialogDescription>Preview rendered from a short-lived in-memory data URL. Use your browser&apos;s Print (Cmd/Ctrl + P) inside the preview if Print didn&apos;t auto-trigger.</DialogDescription>
                </DialogHeader>
                <div className="w-full h-[70vh] bg-gray-50 rounded border border-border overflow-hidden">
                    {preview?.mime?.startsWith("image/")
                        ? <img src={preview.dataUrl} alt="" className="w-full h-full object-contain" />
                        : <iframe
                            src={preview?.dataUrl}
                            title="preview"
                            className="w-full h-full"
                            onLoad={(e) => {
                                if (preview?.printOnLoad) {
                                    try { e.currentTarget.contentWindow?.focus(); e.currentTarget.contentWindow?.print(); }
                                    catch { /* some browsers block cross-frame print; user can Cmd+P */ }
                                }
                            }}
                        />}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function makeBlankWizard() {
    return {
        // ── Shared ────────────────────────────────────────────────────────
        borrower_full_ssn: "",
        borrower_full_ssn_manual: false,
        // ── IDR-specific ──────────────────────────────────────────────────
        spouse_full_ssn: "",
        spouse_full_ssn_manual: true,
        request_reason: "",
        selected_plan: "",
        multiple_servicers: "",
        deferment: "",
        marital_status: "",
        filed_jointly: "",
        spouse_fed_loans: "",
        spouse_name: "",
        spouse_dob: "",
        income_decreased: "",
        taxable_income: "",
        irs_consent: "approve",
        form_date: new Date().toISOString().slice(0, 10),
        // ── PSLF-specific ─────────────────────────────────────────────────
        borrower_dob: "",
        employer_name: "",
        employer_ein: "",
        employer_street: "",
        employer_city: "",
        employer_state: "",
        employer_zip: "",
        employer_phone: "",
        employment_begin: "",
        employment_end: "",
        still_employed: false,
        employment_status: "",
        avg_hours_per_week: "",
        // ── TPD-specific ──────────────────────────────────────────────────
        discharge_basis: "",
    };
}

function computeMissingIDR(client, w) {
    const out = [];
    if (!(client?.client_name || (client?.first_name && client?.last_name))) out.push("borrower_name");
    if (!client?.dob) out.push("dob");
    if (!(client?.address_line_1 || client?.address)) out.push("address");
    if (!client?.city) out.push("city");
    if (!client?.state) out.push("state");
    if (!client?.zip) out.push("zip");
    if (!(client?.phone_e164 || client?.phone_raw)) out.push("phone");
    if (!w.borrower_full_ssn_manual) {
        const digits = (w.borrower_full_ssn || "").replace(/\D/g, "");
        if (digits.length !== 9) out.push("borrower_full_ssn");
    }
    if (!["enter", "recertify", "early_recalc", "change_plan"].includes(w.request_reason)) out.push("request_reason");
    if (w.request_reason !== "early_recalc" && !["ibr", "paye", "icr"].includes(w.selected_plan)) out.push("selected_plan");
    if (!["single", "married", "mfs"].includes(w.marital_status)) out.push("marital_status");
    if (!["yes", "no"].includes(w.taxable_income)) out.push("taxable_income");
    if (!["approve", "do_not"].includes(w.irs_consent)) out.push("irs_consent");
    if (!w.form_date) out.push("form_date");
    return out;
}

function computeMissingPSLF(client, w) {
    const out = [];
    if (!(client?.client_name || (client?.first_name && client?.last_name))) out.push("borrower_name");
    if (!(client?.address_line_1 || client?.address)) out.push("address");
    if (!client?.city) out.push("city");
    if (!client?.state) out.push("state");
    if (!client?.zip) out.push("zip");
    if (!w.borrower_full_ssn_manual) {
        const digits = (w.borrower_full_ssn || "").replace(/\D/g, "");
        if (digits.length !== 9) out.push("borrower_full_ssn");
    }
    if (!(w.borrower_dob || client?.dob)) out.push("borrower_dob");
    if (!(w.employer_name || "").trim()) out.push("employer_name");
    if (!(w.employer_ein || "").trim()) out.push("employer_ein");
    if (!(w.employer_street || "").trim()) out.push("employer_street");
    if (!(w.employer_city || "").trim()) out.push("employer_city");
    if (!(w.employer_state || "").trim()) out.push("employer_state");
    if (!(w.employer_zip || "").trim()) out.push("employer_zip");
    if (!w.employment_begin) out.push("employment_begin");
    if (!w.still_employed && !w.employment_end) out.push("employment_end");
    if (!["full_time", "part_time"].includes(w.employment_status)) out.push("employment_status");
    if (w.avg_hours_per_week === "" || w.avg_hours_per_week == null) out.push("avg_hours_per_week");
    return out;
}

function computeMissingTPD(client, w) {
    const out = [];
    if (!(client?.client_name || (client?.first_name && client?.last_name))) out.push("borrower_name");
    if (!(client?.address_line_1 || client?.address)) out.push("address");
    if (!client?.city) out.push("city");
    if (!client?.state) out.push("state");
    if (!client?.zip) out.push("zip");
    if (!w.borrower_full_ssn_manual) {
        const digits = (w.borrower_full_ssn || "").replace(/\D/g, "");
        if (digits.length !== 9) out.push("borrower_full_ssn");
    }
    if (!(w.borrower_dob || client?.dob)) out.push("borrower_dob");
    if (!["va", "ssa", "medical"].includes(w.discharge_basis)) out.push("discharge_basis");
    return out;
}

// ─── Sub-views ──────────────────────────────────────────────────────────────
function Stepper({ step }) {
    return (
        <ol className="flex items-center gap-2 text-xs" data-testid="wizard-stepper">
            {STEPS.map((s, i) => {
                const active = i === step;
                const done = i < step;
                return (
                    <li key={s.key} className="flex items-center gap-2">
                        <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full border ${active ? "bg-primary text-primary-foreground border-primary" : done ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-500 border-border"}`}>
                            {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="font-semibold">{i + 1}</span>}
                        </span>
                        <span className={`hidden sm:inline ${active ? "text-gray-900 font-medium" : "text-muted-foreground"}`}>{s.title}</span>
                        {i < STEPS.length - 1 && <span className="text-muted-foreground">·</span>}
                    </li>
                );
            })}
        </ol>
    );
}

function TemplatePicker({ templates, selected, onSelect }) {
    if (!templates.length) {
        return <div className="text-sm text-muted-foreground py-8 text-center">Loading templates…</div>;
    }
    return (
        <div className="space-y-2" data-testid="template-picker">
            {templates.map(t => {
                const isSel = selected?.template_key === t.template_key;
                return (
                    <button key={t.template_key} type="button" onClick={() => onSelect(t)}
                        className={`w-full text-left rounded-xl border p-4 transition-all ${isSel ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border hover:border-primary/40 hover:bg-gray-50"}`}
                        data-testid={`template-${t.template_key}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                                <div className="rounded-md bg-primary/10 p-2 mt-0.5"><FileText className="h-5 w-5 text-primary" /></div>
                                <div>
                                    <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                        {t.name}
                                        {t.sensitive && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200"><Lock className="h-3 w-3" />Sensitive</span>}
                                        {t.available
                                            ? <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Available</span>
                                            : <span className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">{t.phase} — coming soon</span>}
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{t.description}</div>
                                    <div className="text-[11px] text-muted-foreground mt-1.5 font-mono">{t.source_pdf_version} · {t.pages} pages</div>
                                </div>
                            </div>
                            {isSel && <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />}
                        </div>
                    </button>
                );
            })}
        </div>
    );
}

function ReviewIDR({ client }) {
    const rows = [
        ["Borrower Name", client?.client_name || `${client?.first_name || ""} ${client?.last_name || ""}`.trim()],
        ["Date of Birth", client?.dob],
        ["Address", [client?.address_line_1 || client?.address, client?.address_line_2].filter(Boolean).join(" ")],
        ["City", client?.city],
        ["State", client?.state],
        ["Zip", client?.zip],
        ["Phone", client?.phone_e164 || client?.phone_raw],
        ["Email", client?.email],
    ];
    return (
        <div className="space-y-3" data-testid="review-idr">
            <div className="text-xs text-muted-foreground">A+ CaseFlow will pre-fill these fields. Edit the client&apos;s Overview to change any missing value.</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {rows.map(([label, val]) => (
                    <div key={label}>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
                        <div className={`text-sm ${val ? "text-gray-900" : "text-amber-700"}`}>{val || "Missing — will block generation"}</div>
                    </div>
                ))}
            </div>
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2">
                Full SSN is <strong>not</strong> stored in A+ CaseFlow. You&apos;ll enter it in the next step; it&apos;s used only to stamp the PDF and is discarded after.
            </div>
        </div>
    );
}

function CompleteIDR({ w, setW, missing }) {
    const set = (k, v) => setW({ ...w, [k]: v });
    const isMarried = w.marital_status === "married" || w.marital_status === "mfs";
    return (
        <div className="space-y-4" data-testid="complete-idr">
            {missing.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2" data-testid="missing-required">
                    <strong>{missing.length} required field{missing.length === 1 ? "" : "s"} missing:</strong> {missing.map(m => m.replace(/_/g, " ")).join(", ")}.
                </div>
            )}

            <Section title="Borrower SSN (sensitive — not stored)">
                <div className="flex items-center gap-2">
                    <Input placeholder="123-45-6789" value={w.borrower_full_ssn} onChange={e => set("borrower_full_ssn", e.target.value)}
                        disabled={w.borrower_full_ssn_manual} data-testid="w-ssn"
                        autoComplete="off" autoCorrect="off" spellCheck={false} />
                    <label className="text-xs flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
                        <input type="checkbox" checked={w.borrower_full_ssn_manual} onChange={e => set("borrower_full_ssn_manual", e.target.checked)} data-testid="w-ssn-manual" />
                        Complete manually
                    </label>
                </div>
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Sel label="Request Reason" testId="w-reason" value={w.request_reason} onChange={v => set("request_reason", v)} options={REQUEST_REASONS} />
                <Sel label="Selected Plan" testId="w-plan" value={w.selected_plan} onChange={v => set("selected_plan", v)} options={PLANS} disabled={w.request_reason === "early_recalc"} />
                <Sel label="Marital Status" testId="w-marital" value={w.marital_status} onChange={v => set("marital_status", v)} options={[{ v: "single", l: "Single" }, { v: "married", l: "Married" }, { v: "mfs", l: "Married, filing separately" }]} />
                <Sel label="Taxable Income (last year)?" testId="w-taxable" value={w.taxable_income} onChange={v => set("taxable_income", v)} options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }]} />
                <Sel label="Multiple Servicers?" value={w.multiple_servicers} onChange={v => set("multiple_servicers", v)} options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }]} />
                <Sel label="In Deferment / Forbearance?" value={w.deferment} onChange={v => set("deferment", v)} options={[{ v: "no", l: "No" }, { v: "yes_pay_now", l: "Yes — pay during" }, { v: "yes_wait_for_df", l: "Yes — wait until def/forb ends" }]} />
                <Sel label="Income Decreased Recently?" value={w.income_decreased} onChange={v => set("income_decreased", v)} options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }, { v: "no_recent", l: "Haven't filed recently" }]} />
                <Sel label="IRS Consent" testId="w-irs" value={w.irs_consent} onChange={v => set("irs_consent", v)} options={[{ v: "approve", l: "Approve (recommended)" }, { v: "do_not", l: "Do NOT approve" }]} />
                {isMarried && (
                    <Sel label="Filed Jointly?" value={w.filed_jointly} onChange={v => set("filed_jointly", v)} options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }]} />
                )}
                {isMarried && (
                    <Sel label="Spouse Has Federal Loans?" value={w.spouse_fed_loans} onChange={v => set("spouse_fed_loans", v)} options={[{ v: "yes", l: "Yes" }, { v: "no", l: "No" }]} />
                )}
                {isMarried && (
                    <Section title="Spouse Name (optional)"><Input value={w.spouse_name} onChange={e => set("spouse_name", e.target.value)} /></Section>
                )}
                {isMarried && (
                    <Section title="Spouse DOB (optional)"><Input type="date" value={w.spouse_dob} onChange={e => set("spouse_dob", e.target.value)} /></Section>
                )}
                <Section title="Form Date">
                    <Input type="date" value={w.form_date} onChange={e => set("form_date", e.target.value)} data-testid="w-form-date" />
                </Section>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 text-xs px-3 py-2">
                Borrower signature is intentionally left blank for wet signature. Spouse SSN defaults to <strong>Complete Manually</strong> on the printed form.
            </div>
        </div>
    );
}

/* ── PSLF Step-1 review ───────────────────────────────────────────────── */
function ReviewPSLF({ client }) {
    const rows = [
        ["Borrower Name", client?.client_name || `${client?.first_name || ""} ${client?.last_name || ""}`.trim()],
        ["Address", [client?.address_line_1, client?.address_line_2].filter(Boolean).join(" ") || client?.address],
        ["City / State / Zip", [client?.city, client?.state, client?.zip].filter(Boolean).join(", ")],
        ["Phone", client?.phone_e164 || client?.phone_raw],
        ["Email", client?.email],
        ["DOB on profile", client?.dob || "— enter in next step"],
    ];
    return (
        <div className="space-y-2" data-testid="review-pslf">
            <div className="text-sm text-gray-700">
                These borrower fields will be auto-filled in <strong>Section 1</strong> of the PSLF / TEPSLF application.
                You&apos;ll enter the full SSN and the employer details in the next step.
            </div>
            <dl className="border border-border rounded-md overflow-hidden divide-y divide-border">
                {rows.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 text-sm">
                        <dt className="bg-gray-50 px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">{k}</dt>
                        <dd className="col-span-2 px-3 py-2 text-gray-900">{v || <span className="text-amber-700">— missing</span>}</dd>
                    </div>
                ))}
            </dl>
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2">
                Full SSN is <strong>not</strong> stored in A+ CaseFlow. You&apos;ll enter it in the next step; it&apos;s used only to stamp the PDF and is discarded after.
            </div>
        </div>
    );
}

/* ── PSLF Step-2 complete ──────────────────────────────────────────────── */
function CompletePSLF({ w, setW, client, missing }) {
    const set = (k, v) => setW({ ...w, [k]: v });
    return (
        <div className="space-y-4" data-testid="complete-pslf">
            {missing.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2" data-testid="missing-required">
                    <strong>{missing.length} required field{missing.length === 1 ? "" : "s"} missing:</strong> {missing.map(m => m.replace(/_/g, " ")).join(", ")}.
                </div>
            )}

            <Section title="Borrower SSN (sensitive — not stored)">
                <div className="flex items-center gap-2">
                    <Input placeholder="123-45-6789" value={w.borrower_full_ssn} onChange={e => set("borrower_full_ssn", e.target.value)}
                        disabled={w.borrower_full_ssn_manual} data-testid="w-pslf-ssn"
                        autoComplete="off" autoCorrect="off" spellCheck={false} />
                    <label className="text-xs flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
                        <input type="checkbox" checked={w.borrower_full_ssn_manual} onChange={e => set("borrower_full_ssn_manual", e.target.checked)} data-testid="w-pslf-ssn-manual" />
                        Complete manually
                    </label>
                </div>
            </Section>

            <Section title="Borrower DOB">
                <Input type="date" value={w.borrower_dob || client?.dob || ""} onChange={e => set("borrower_dob", e.target.value)} data-testid="w-pslf-dob" />
            </Section>

            <div className="text-[11px] uppercase tracking-wider text-muted-foreground pt-2">Employer (Section 4)</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Section title="Employer Name">
                    <Input value={w.employer_name} onChange={e => set("employer_name", e.target.value)} data-testid="w-emp-name" />
                </Section>
                <Section title="Federal EIN (FEIN/EIN)">
                    <Input placeholder="12-3456789" value={w.employer_ein} onChange={e => set("employer_ein", e.target.value)} data-testid="w-emp-ein" />
                </Section>
                <Section title="Street Address">
                    <Input value={w.employer_street} onChange={e => set("employer_street", e.target.value)} data-testid="w-emp-street" />
                </Section>
                <Section title="Employer Phone (optional)">
                    <Input value={w.employer_phone} onChange={e => set("employer_phone", e.target.value)} data-testid="w-emp-phone" />
                </Section>
                <Section title="City">
                    <Input value={w.employer_city} onChange={e => set("employer_city", e.target.value)} data-testid="w-emp-city" />
                </Section>
                <Section title="State">
                    <Input maxLength={2} value={w.employer_state} onChange={e => set("employer_state", e.target.value.toUpperCase())} data-testid="w-emp-state" />
                </Section>
                <Section title="ZIP">
                    <Input value={w.employer_zip} onChange={e => set("employer_zip", e.target.value)} data-testid="w-emp-zip" />
                </Section>
                <Section title="Employment Begin Date">
                    <Input type="date" value={w.employment_begin} onChange={e => set("employment_begin", e.target.value)} data-testid="w-emp-begin" />
                </Section>
                <Section title="Employment End Date">
                    <Input type="date" value={w.employment_end} onChange={e => set("employment_end", e.target.value)} disabled={!!w.still_employed} data-testid="w-emp-end" />
                </Section>
                <Section title="Still Employed?">
                    <label className="flex items-center gap-2 text-sm pt-2">
                        <input type="checkbox" checked={!!w.still_employed} onChange={e => set("still_employed", e.target.checked)} data-testid="w-emp-still" />
                        <span>Yes — still working there</span>
                    </label>
                </Section>
                <Sel label="Employment Status" testId="w-emp-status" value={w.employment_status} onChange={v => set("employment_status", v)}
                     options={[{ v: "full_time", l: "Full-Time" }, { v: "part_time", l: "Part-Time" }]} />
                <Section title="Average Hours / Week">
                    <Input type="number" min="1" max="168" value={w.avg_hours_per_week} onChange={e => set("avg_hours_per_week", e.target.value)} data-testid="w-emp-hours" />
                </Section>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 text-xs px-3 py-2">
                Borrower, employer (Section 5A), and any official signatures are intentionally left blank for wet signature on the printed form.
            </div>
        </div>
    );
}

/* ── TPD Step-1 review ────────────────────────────────────────────────── */
function ReviewTPD({ client }) {
    const rows = [
        ["Applicant Name", client?.client_name || `${client?.first_name || ""} ${client?.last_name || ""}`.trim()],
        ["Address", [client?.address_line_1, client?.address_line_2].filter(Boolean).join(" ") || client?.address],
        ["City / State / Zip", [client?.city, client?.state, client?.zip].filter(Boolean).join(", ")],
        ["Phone", client?.phone_e164 || client?.phone_raw],
        ["Email", client?.email],
        ["DOB on profile", client?.dob || "— enter in next step"],
    ];
    return (
        <div className="space-y-2" data-testid="review-tpd">
            <div className="text-sm text-gray-700">
                These applicant fields will be auto-filled in <strong>Section 3</strong> of the TPD Discharge Application.
                You&apos;ll enter the full SSN and pick the discharge basis in the next step.
            </div>
            <dl className="border border-border rounded-md overflow-hidden divide-y divide-border">
                {rows.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-3 text-sm">
                        <dt className="bg-gray-50 px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground">{k}</dt>
                        <dd className="col-span-2 px-3 py-2 text-gray-900">{v || <span className="text-amber-700">— missing</span>}</dd>
                    </div>
                ))}
            </dl>
            <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2">
                <strong>Medical / VA / SSA documentation</strong> is attached manually and not collected by this wizard. Sections 5 (Borrower Signature), 6 (Medical Professional), 8 (VA), and 9 (SSA) are left blank for wet sign / manual completion.
            </div>
        </div>
    );
}

/* ── TPD Step-2 complete ──────────────────────────────────────────────── */
function CompleteTPD({ w, setW, client, missing }) {
    const set = (k, v) => setW({ ...w, [k]: v });
    const BASIS_OPTIONS = [
        { v: "va",      l: "VA Determination", help: "100%-disabling service-connected disability or individual unemployability rating." },
        { v: "ssa",     l: "SSA Determination", help: "SSDI / SSI / SSA retirement with a qualifying disability determination." },
        { v: "medical", l: "Medical Professional Certification", help: "Licensed medical professional must complete Section 6 manually." },
    ];
    return (
        <div className="space-y-4" data-testid="complete-tpd">
            {missing.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2" data-testid="missing-required">
                    <strong>{missing.length} required field{missing.length === 1 ? "" : "s"} missing:</strong> {missing.map(m => m.replace(/_/g, " ")).join(", ")}.
                </div>
            )}

            <Section title="Applicant SSN (sensitive — not stored)">
                <div className="flex items-center gap-2">
                    <Input placeholder="123-45-6789" value={w.borrower_full_ssn} onChange={e => set("borrower_full_ssn", e.target.value)}
                        disabled={w.borrower_full_ssn_manual} data-testid="w-tpd-ssn"
                        autoComplete="off" autoCorrect="off" spellCheck={false} />
                    <label className="text-xs flex items-center gap-1.5 whitespace-nowrap cursor-pointer">
                        <input type="checkbox" checked={w.borrower_full_ssn_manual} onChange={e => set("borrower_full_ssn_manual", e.target.checked)} data-testid="w-tpd-ssn-manual" />
                        Complete manually
                    </label>
                </div>
            </Section>

            <Section title="Applicant DOB">
                <Input type="date" value={w.borrower_dob || client?.dob || ""} onChange={e => set("borrower_dob", e.target.value)} data-testid="w-tpd-dob" />
            </Section>

            <div className="text-[11px] uppercase tracking-wider text-muted-foreground pt-2">Discharge Basis (Section 4)</div>
            <div className="space-y-2" data-testid="w-tpd-basis-group">
                {BASIS_OPTIONS.map(opt => (
                    <label key={opt.v} className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition ${w.discharge_basis === opt.v ? "border-emerald-500 bg-emerald-50" : "border-border hover:bg-gray-50"}`} data-testid={`tpd-basis-${opt.v}`}>
                        <input type="radio" name="tpd_basis" className="mt-1" checked={w.discharge_basis === opt.v} onChange={() => set("discharge_basis", opt.v)} />
                        <div>
                            <div className="text-sm font-medium text-gray-900">{opt.l}</div>
                            <div className="text-xs text-muted-foreground">{opt.help}</div>
                        </div>
                    </label>
                ))}
            </div>

            <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-900 text-xs px-3 py-2">
                <strong>Medical details (diagnosis, treatment history, prognosis) are NOT collected by this wizard.</strong> The physician completes Section 6 directly on the printed form. Borrower / agency / certifier signatures are also left blank for wet signature.
            </div>
        </div>
    );
}

function ResultPanel({ result, onDownload, onPreview, onPrint }) {
    return (
        <div className="space-y-4 text-center py-6" data-testid="result-panel">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-100 text-emerald-700">
                <CheckCircle2 className="h-7 w-7" />
            </div>
            <div className="text-base font-semibold text-gray-900">{result.filename}</div>
            <div className="text-xs text-muted-foreground">
                Saved to the Documents tab as <span className="font-mono">{result.document_status}</span> · {Math.round(result.size_bytes / 1024)} KB · <span className="text-rose-700 font-semibold">Sensitive</span>
            </div>
            <div className="flex items-center justify-center gap-2 pt-2">
                <Button size="sm" variant="outline" onClick={onPreview} data-testid="result-preview"><Eye className="h-4 w-4 mr-1.5" /> Preview</Button>
                <Button size="sm" onClick={onDownload} data-testid="result-download"><Download className="h-4 w-4 mr-1.5" /> Download</Button>
                <Button size="sm" variant="outline" onClick={onPrint} data-testid="result-print"><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
            </div>
        </div>
    );
}

function ComingSoonNotice({ template }) {
    return (
        <div className="rounded-md border border-dashed border-border p-6 text-center" data-testid="coming-soon">
            <AlertCircle className="h-6 w-6 text-amber-600 mx-auto mb-2" />
            <div className="text-sm font-medium text-gray-900">{template?.name} generation is coming in {template?.phase}.</div>
            <div className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">The wizard shell is live; the PDF stamping for this template ships next.</div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <label className="block space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{title}</div>
            {children}
        </label>
    );
}

function Sel({ label, value, onChange, options, testId, disabled = false }) {
    return (
        <label className="block space-y-1">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <Select value={value || "__none"} onValueChange={(v) => onChange(v === "__none" ? "" : v)} disabled={disabled}>
                <SelectTrigger data-testid={testId}><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="__none">—</SelectItem>
                    {options.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                </SelectContent>
            </Select>
        </label>
    );
}
